import React from 'react'
import { useConfig } from 'msp-integrations'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import Availability from '@atoms/Availability/Availability'
import Headline from '@atoms/Headline/Headline'
import isEmpty from 'lodash/isEmpty'
import LineItemPromotion from './LineItemPromotion'
import CustomLink from '@atoms/CustomLink/CustomLink'
import * as T from './LineItem.types'
import * as S from './LineItem.styles'
import { BonusDiscountLineItem } from './LineItemPromotion.types'

const LineItem = ({
  id,
  thumbnailUrl,
  fallBackImage,
  productName,
  purchasable = true,
  productDescription,
  productPromotionMessage,
  currency,
  priceAdjustments,
  priceTag,
  productQuantity,
  productUrl,
  inventory,
  commerceBox,
  removePromoCode = () => {},
  handleBonusDiscountLineItem = () => {},
  bonusItemsByCouponCode = new Map<string, BonusDiscountLineItem>(),
  couponItems,
  overview = false,
  onClick,
  inline = false
}: T.LineItemProps): React.ReactElement => {
  const { ats, orderable, preorderable, stockLevel, inStockDate } =
    inventory || {}
  const {
    theme: {
      mspComponents: { headlineFont }
    }
  } = useConfig()
  const styleType = inline ? 'inline' : 'wrap'
  const handleImageNotFound = (el: HTMLImageElement) => {
    if (el.src !== fallBackImage) {
      el.src = fallBackImage
    }
  }

  return (
    <div key={id} className={S.LineItemWrapper}>
      <div className={S.LineItemRow[overview ? 'overview' : styleType]}>
        <div className='max-w-[150px] overflow-hidden'>
          <CustomLink
            href={productUrl}
            inline={true}
            isDisabled={!purchasable}
            onClick={onClick}
            title={productName}
          >
            <LazyLoadImage
              src={thumbnailUrl}
              alt={productName}
              width='150'
              height='150'
              onError={(ev) =>
                handleImageNotFound(ev.target as HTMLImageElement)
              }
            />
          </CustomLink>
        </div>
        <div className={S.LineItemContent[styleType]}>
          <Headline tag='h6' className={headlineFont}>
            <CustomLink
              href={productUrl}
              inline={true}
              isDisabled={!purchasable}
              onClick={onClick}
            >
              {productName}
            </CustomLink>
          </Headline>
          <p className={S.LineItemProductDescription[styleType]}>
            {productDescription || productPromotionMessage || ''}
          </p>
          {!isEmpty(inventory) && (
            <Availability
              ats={ats}
              stockLevel={stockLevel}
              inStockDate={inStockDate}
              orderable={orderable}
              preorderable={preorderable}
            />
          )}
        </div>
        {commerceBox && inline && (
          <div className='justify-between self-center justify-self-end py-3'>
            {commerceBox}
          </div>
        )}
        {overview && inline && (
          <>
            <div className='flex items-center justify-center'>
              <p className='lineitem-product-quantity'>{productQuantity}</p>
            </div>
            <div className='flex items-center justify-center'>{priceTag}</div>
          </>
        )}
      </div>
      {commerceBox && !inline && <div className='py-3'>{commerceBox}</div>}
      {overview && !inline && (
        <div className='flex py-3'>
          <p className='w-[150px] text-center'>{productQuantity}</p>
          {priceTag}
        </div>
      )}
      {priceAdjustments && currency && (
        <LineItemPromotion
          priceAdjustments={priceAdjustments}
          currency={currency}
          removePromoCode={removePromoCode}
          handleBonusDiscountLineItem={handleBonusDiscountLineItem}
          bonusItemsByCouponCode={bonusItemsByCouponCode}
          couponItems={couponItems}
          readOnly={overview}
        />
      )}
    </div>
  )
}

export default LineItem
